import { Fragment, useCallback } from "react"
import type { FC, ReactElement } from "react"

import { QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query"
import isUndefined from "lodash-es/isUndefined"
import { useMount, useShallowCompareEffect } from "react-use"

import { ECoachingSessionsGroupQueryKey } from "@/3514/config"
import { AttendanceModal } from "@/3514/modals"
import {
    type TCoachingSessionsGroupSuspenseQuery,
    coachingSessionsQueryLiveSessionAdder,
    coachingSessionsQueryLiveSessionRemover,
    useCoachingSessionsGroupSuspenseQuery
} from "@/3514/queries"
import { useAnyModalVisible } from "@/3514/store/modal"
import { EUserRole } from "@/3514/types"
import { minutes } from "@/3514/utils/time.utils"
import { type TEmptyAsyncCallback, emptyRenderCallback } from "@/shared/types/functions"

import CoachingSessionsView from "../coaching-sessions.view"
import modals from "../modals"
import type { TCoachingSessionsEntityProps as TProps } from "../types"

function renderModals(): ReactElement {
    return (
        <Fragment>
            <AttendanceModal />

            <modals.SessionReschedulesModal />

            <modals.CancelledSessionModal />
        </Fragment>
    )
}

const CoachingSessionsContainerGroup: FC<TProps> = ({
    user,
    onMount = undefined,
    onQuerySuccess = undefined,
    groupId,
    wrapperClassName = undefined
}: TProps): ReactElement => {
    const queryKey: QueryKey = ECoachingSessionsGroupQueryKey({ groupId })

    const isAnyModalVisible: boolean = useAnyModalVisible()

    useMount((): void => {
        if (!isUndefined(onMount)) onMount()
    })

    const qc: QueryClient = useQueryClient()

    const {
        data: sessions,
        dataUpdatedAt: sessionsUpdatedAt,
        isSuccess: isQuerySuccess
    }: TCoachingSessionsGroupSuspenseQuery = useCoachingSessionsGroupSuspenseQuery({
        refetchInterval: minutes(15),
        refetchOnWindowFocus: isAnyModalVisible ? true : "always",
        refetchOnMount: true,
        groupId
    })

    useShallowCompareEffect((): void => {
        if (isQuerySuccess) onQuerySuccess?.(sessions)
    }, [isQuerySuccess, sessions, onQuerySuccess])

    const onLiveSessionStarted: TEmptyAsyncCallback = useCallback(
        async (): Promise<void> => await coachingSessionsQueryLiveSessionAdder({ queryKey }, { qc }),
        [qc, queryKey]
    )

    const onLiveSessionPassed: TEmptyAsyncCallback = useCallback(
        async (): Promise<void> => await coachingSessionsQueryLiveSessionRemover({ queryKey }, { qc }),
        [qc, queryKey]
    )

    return (
        <CoachingSessionsView
            sessions={sessions}
            sessionsUpdatedAt={sessionsUpdatedAt}
            user={user}
            sessionsQueryKey={queryKey}
            sessionInfoRole={EUserRole.Coach}
            onLiveSessionStarted={onLiveSessionStarted}
            onLiveSessionPassed={onLiveSessionPassed}
            wrapperClassName={wrapperClassName}
            withSwappableSessions={false}
            withGiftSessions={false}
            withAttendanceMarkableSessions
            withPostSessionModalOpeners={false}
            renderScheduler={emptyRenderCallback}
            renderModals={renderModals}
        />
    )
}

CoachingSessionsContainerGroup.displayName = "CoachingSessionsGroupContainer"

export default CoachingSessionsContainerGroup
